import { get } from 'lodash';
import { ApplicationPayload } from '~/types/ApplicationPayload';

const emiratesIdOptionalFields = (n: (name: string) => string, passportFilled?: boolean | undefined) => {
  if (passportFilled) {
    return {
      [n('emiratesId.files')]: '',
      [n('emiratesId.expiryDate')]: '',
      [n('emiratesId.documentNumber')]: '',
    };
  }
  return {};
};

const passportOptionalFields = (n: (name: string) => string, eidFilled?: boolean | undefined) => {
  if (eidFilled) {
    return {
      [n('passport.files')]: '',
      [n('passport.expiryDate')]: '',
      [n('passport.documentNumber')]: '',
    };
  }
  return {};
};

const personOptionalFields = (payload: ApplicationPayload, name: string) => {
  function n(path: string) {
    return [name, path].join('.');
  }
  const passport = get(payload, n('passport'));
  const eid = get(payload, n('emiratesId'));

  const isPassportFilled = isInputPopulated(passport);
  const isEidFilled = isInputPopulated(eid);

  return {
    ...emiratesIdOptionalFields(n, isPassportFilled),
    ...passportOptionalFields(n, isEidFilled),
    [n('poBox')]: '',
  };
};

const isInputPopulated = (input: string) => {
  return (
    (input &&
      Object.values(input)
        ?.filter((value: any) => value)
        ?.filter((value: any) => value?.length > 0)?.length > 0) ||
    undefined
  );
};

const personShareholdersOptionalFields = (name: string) => {
  function n(path: string) {
    return [name, path].join('.');
  }
  return {
    // [n('emiratesId.files')]: '',
    // [n('emiratesId.expiryDate')]: '',
    // [n('emiratesId.documentNumber')]: '',
    [n('visa.files')]: '',
    [n('visa.expiryDate')]: '',
    [n('poBox')]: '',
  };
};

const businessShareholdersOptionalFields = (name: string) => {
  const n = (path: string) => [name, path].join('.');
  return {};
};

const usersOptionalFields = (name: string, passportFilled?: boolean | undefined, eidFilled?: boolean | undefined) => {
  function n(path: string) {
    return [name, path].join('.');
  }
  return {
    ...emiratesIdOptionalFields(n, passportFilled),
    ...passportOptionalFields(n, eidFilled),
    [n('secondName')]: '',
  };
};

export default {
  companyInformation: () => ({}),
  addresses: () => {
    return {
      'addresses.headOffice.website': '',
      'addresses.headOffice.landmark': '',
      'addresses.mailingAddress.website': '',
      'addresses.mailingAddress.landmark': '',
    };
  },
  soleProprietorAuthorizedSignatory: (payload) => {
    return personOptionalFields(payload, 'authorizedRepresentative.0');
  },
  multipleAuthorizedSignatory: (payload: any) => {
    const authorizedRepresentativeCount = (payload?.authorizedRepresentative ?? []).length;

    let optionalFields: Record<string, string> = {};
    for (let i = 0; i <= authorizedRepresentativeCount; i++) {
      optionalFields = {
        ...optionalFields,
        ...personOptionalFields(payload, `authorizedRepresentative.${i}`),
      };
    }
    return optionalFields;
  },
  usersOptional: (payload: any, passportFilled?: boolean | undefined, eidFilled?: boolean | undefined) => {
    const selfServiceUsersCount = (payload?.users.selfServiceUsers ?? []).length;
    const VirtualCardUsersCount = (payload?.users.dataExpenseManagementAndVirtualCardUsers ?? []).length;

    let optionalFields: Record<string, string> = {};

    // Self Service Users Marks fields
    for (let i = 0; i <= selfServiceUsersCount; i++) {
      optionalFields = {
        ...optionalFields,
        ...usersOptionalFields(`users.${i}.data`, passportFilled, eidFilled),
      };
    }

    // Virtual Card Users Marks fields
    for (let i = 0; i <= VirtualCardUsersCount; i++) {
      optionalFields = {
        ...optionalFields,
        ...usersOptionalFields(`users.${i}.data`, passportFilled, eidFilled),
      };
    }

    return optionalFields;
  },
  multipleShareholders: (payload) => {
    const individualsCount = (payload.shareholders?.individuals ?? []).length;
    const beneficialOwnersCount = (payload.shareholders?.beneficialOwners ?? []).length;
    const businessesCount = (payload.shareholders?.businesses ?? []).length;

    let optionalFields: Record<string, string> = {
      'shareholders.additionalInfo.files': '',
      'shareholders.additionalInfo.comment': '',
    };

    // Individual shareholder optional fields
    for (let i = 0; i <= individualsCount; i++) {
      optionalFields = {
        ...optionalFields,
        ...personOptionalFields(payload, `shareholders.individuals.${i}.data`),
      };
    }

    // Benefical shareholder optional fields
    for (let i = 0; i <= beneficialOwnersCount; i++) {
      optionalFields = {
        ...optionalFields,
        ...personOptionalFields(payload, `shareholders.beneficialOwners.${i}.data`),
      };
    }

    // Businesses shareholder optional fields
    for (let i = 0; i <= businessesCount; i++) {
      optionalFields = {
        ...optionalFields,
        ...businessShareholdersOptionalFields(`shareholders.businesses.${i}.data`),
      };
    }

    return optionalFields;
  },
  additionalInformation: () => {
    const result: Record<string, string> = {
      'additionalInformation.companyInformation.website': '',
      'additionalInformation.companyInformation.primaryLocation': '',
      'additionalInformation.companyInformation.tradeArea': '',
      // 'additionalInformation.companyInformation.numberOfEmployees': '',
    };
    return result;
  },
  bankAccount: () => ({}),
} as {
  [x: string]: (
    payload: ApplicationPayload,
    passportFilled?: boolean | undefined,
    eidFilled?: boolean | undefined,
  ) => Record<string, string>;
};
